"use client";

import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/utils";

const inputVariants = cva(
  "relative flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
);

const inputSectionClasses = cva(
  "absolute top-1/2 z-[1] -translate-y-[47%] focus-within:opacity-100 focus-within:[outline:none] [&>button]:block [&>button]:[outline:none] [&>button]:focus-visible:[outline:none]",

  {
    variants: {
      side: {
        left: "left-2.5",
        right: "right-2.5",
      },
      faded: {
        true: "opacity-50",
        false: "opacity-100",
      },
      pointerEvents: {
        true: "pointer-events-auto hover:opacity-100",
        false: "pointer-events-none",
      },
      pointer: {
        true: "cursor-pointer",
        false: "cursor-auto",
      },
    },
    defaultVariants: {
      faded: true,
      pointerEvents: false,
      pointer: false,
    },
  },
);

interface InputProps
  extends React.ComponentProps<"input">, VariantProps<typeof inputVariants> {
  classes?: {
    wrapper?: string;
  };
  sectionLeft?: React.ReactNode;
  sectionLeftPointerEvents?: VariantProps<
    typeof inputSectionClasses
  >["pointerEvents"];
  sectionLeftPointer?: VariantProps<typeof inputSectionClasses>["pointer"];
  sectionRight?: React.ReactNode;
  sectionRightPointerEvents?: VariantProps<
    typeof inputSectionClasses
  >["pointerEvents"];
  sectionRightPointer?: VariantProps<typeof inputSectionClasses>["pointer"];
}

/**
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/input
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      sectionLeft,
      sectionLeftPointerEvents,
      sectionLeftPointer,
      sectionRight,
      sectionRightPointerEvents,
      sectionRightPointer,
      classes = {},

      ...props
    },
    ref,
  ) => (
    <div className={cn("relative w-full", classes.wrapper)}>
      {sectionLeft && (
        <div
          className={inputSectionClasses({
            side: "left",
            pointerEvents: sectionLeftPointerEvents,
            pointer: sectionLeftPointer,
          })}
        >
          {sectionLeft}
        </div>
      )}

      <input
        ref={ref}
        type={type}
        {...props}
        className={cn(
          inputVariants(),
          !!sectionLeft && "pl-[2.125rem]",
          !!sectionRight && "pr-[2.125rem]",
          className,
        )}
      />

      {sectionRight && (
        <div
          className={inputSectionClasses({
            side: "right",
            pointerEvents: sectionRightPointerEvents,
            pointer: sectionRightPointer,
          })}
        >
          {sectionRight}
        </div>
      )}
    </div>
  ),
);
Input.displayName = "Input";

export { Input, inputSectionClasses, inputVariants, type InputProps };
